import type { AxiosResponse } from "axios";
import api from "../api";

import {
  SET_CURRENT_USER,
  CLEAR_CURRENT_PROFILE,
  PROFILE_LOADING,
  GET_PROFILE,
  VERIF_PASS,
  UPDATING_PASS,
  UPDATE_STATUS,
} from "./userTypes";
import { GET_ERRORS } from "../errorServices/errorTypes";
import { CURRENT_PERMISSION } from "./types";
import { asCaught, errorPayload } from "../caughtError";

import type {
  AuthUserResponse,
  MessageResponse,
  PasswordStatusResponse,
  UserResponse,
  UserEnableResponse,
} from "../../../../shared/apiTypes";

// Login – session via HTTP-only cookies + user in response body
export const loginUser = (userData) => async (dispatch) => {
  try {
    const res: AxiosResponse<AuthUserResponse> = await api.post("/api/users/auth", userData);
    const user = res?.data?.user;

    if (!user || typeof user.role !== "string") {
      dispatch({
        type: GET_ERRORS,
        payload: {
          errors: [{ msg: "Unexpected login response. Please try again." }],
        },
      });
      return;
    }

    localStorage.setItem("role", user.role);
    dispatch(setCurrentUser({ user }));
    await dispatch(verifPassord());
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: asCaught(err).response ? asCaught(err).response?.data : "An error occurred",
    });
  }
};

// Set logged in user
export const setCurrentUser = (decoded) => {
  return {
    type: SET_CURRENT_USER,
    payload: decoded,
  };
};
// Set current permission
export const setCurrentPermission = (permission) => {
  const permissions = permission?.permissions;
  const siteId = permission?.site_id;
  if (!Array.isArray(permissions) || !siteId) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[setCurrentPermission] Invalid payload:", permission);
    }
  }

  return {
    type: CURRENT_PERMISSION,
    payload: permission,
  };
};

// Log user out
export const logoutUser = () => async (dispatch) => {
  try {
    await api.post<MessageResponse>("/api/users/logout");
  } catch (e) {
    // ignore logout endpoint failures and clear client auth state anyway
  }
  // SCRUM-1471 removed the only writer of `currentsite` (SiteSelector); the current
  // site comes from the request Host, so there is nothing here to clear.

  //Remove role from localStorage
  localStorage.removeItem("role");

  //remove permission localstorage
  localStorage.removeItem("permission");
  // Set current user to {} which will set isAuthenticated to false
  dispatch(setCurrentUser({}));
  dispatch({ type: VERIF_PASS, payload: false });
};

// Clear profile
export const clearCurrentProfile = () => {
  return {
    type: CLEAR_CURRENT_PROFILE,
  };
};

//get current profile
export const getProfile = () => (dispatch) => {
  dispatch(setProfileLoading());
  api
    .get("/api/profile/profile")
    .then((res) =>{
      dispatch({
        type: GET_PROFILE,
        payload: res.data,
      })
    }
    )
    .catch((err) =>
      // SCRUM-856: carry the real error to the hub instead of a null payload it
      // ignores — a failed profile load was invisible.
      dispatch({
        type: GET_ERRORS,
        payload: err?.response?.data ?? { message: err?.message },
      })
    );
};

// Profile loading
const setProfileLoading = () => {
  return {
    type: PROFILE_LOADING,
  };
};

export const verifPassord = () => async (dispatch) => {
  try {
    const res: AxiosResponse<PasswordStatusResponse> = await api.get(
      "/api/users/verifpass"
    );
    const stillDefault = res.data === true;
    dispatch({
      type: VERIF_PASS,
      payload: stillDefault,
    });
    return stillDefault;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: null,
    });
    return false;
  }
};

export const updatePassword = (userData) => async (dispatch) => {
  try {
    const res: AxiosResponse<UserResponse> = await api.post(
      "/api/users/updatepassword",
      userData
    );
    dispatch({
      type: UPDATING_PASS,
      payload: res.data,
    });
    await dispatch(verifPassord());
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: asCaught(err).response ? asCaught(err).response?.data : null,
    });
    throw err;
  }
};

export const changeStatus = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<UserEnableResponse> = await api.put(
      `/api/users/enableuser/${data._id}`,
      data
    );
    dispatch({
      type: UPDATE_STATUS,
      payload: res.data,
    });
    return res.data;
  } catch (err) {
    // Surface the failure + re-throw (SCRUM-815) so the caller does not apply the
    // optimistic list update when the status PUT actually failed.
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
    throw err;
  }
};
